import { ECardinality } from "@kuika/kuika-cl-model";

export interface IActionParameterLabel {
  /** The parameter's display name, falling back to its raw name. */
  name: string;
  /** The last segment of `Typename` (e.g. "String" out of "System.String"). */
  typeName: string;
  /**
   * Only set when the parameter's cardinality is NOT the default single ("One") value — e.g.
   * "Many". Undefined single-cardinality parameters render no second chip.
   */
  cardinalityLabel?: string;
}

/**
 * What a parameter label reads. An `IActionParameter` fits as it is; the special symbols groups also
 * label things that are not in `BackEndValueType` ("Object" for a for-each result, a screen input's
 * or a document parameter's free-form type name), so the type is a plain string here.
 */
export interface IActionParameterLabelSource {
  displayName?: string;
  name: string;
  Typename?: string;
  cardinality?: ECardinality;
}

export type ActionParameterLabelSource = IActionParameterLabelSource;

/**
 * Splits an action parameter's title into a name, a type chip and an optional cardinality chip.
 * Previously the title was a single string: `${displayName ?? name} (${typeName} - ${cardinality})`.
 * This only formats the pieces — the caller (`action-symbol-with-child.tsx`) still decides whether
 * to show the type/cardinality chips at all (its existing `parameterOptions === undefined ||
 * p.isEnum === true` gate is unrelated to this helper and must stay at the call site).
 */
export const getActionParameterLabel = (parameter: ActionParameterLabelSource): IActionParameterLabel => {
  const name: string = parameter.displayName ?? parameter.name;
  const typeName: string = parameter.Typename?.split(".").slice(-1).pop() ?? "";
  const cardinalityLabel: string | undefined =
    parameter.cardinality !== undefined && parameter.cardinality !== ECardinality.One
      ? ECardinality[parameter.cardinality]
      : undefined;
  return { name, typeName, cardinalityLabel };
};

/** The colour family of a parameter's type chip. */
export type ParameterTypeTone = "text" | "number" | "boolean" | "date" | "id" | "other";

const TYPE_TONES: Record<string, ParameterTypeTone> = {
  string: "text",
  char: "text",
  int16: "number",
  int32: "number",
  int64: "number",
  uint16: "number",
  uint32: "number",
  uint64: "number",
  int: "number",
  long: "number",
  short: "number",
  byte: "number",
  decimal: "number",
  double: "number",
  single: "number",
  float: "number",
  number: "number",
  boolean: "boolean",
  bool: "boolean",
  date: "date",
  datetime: "date",
  datetimeoffset: "date",
  dateonly: "date",
  timeonly: "date",
  timespan: "date",
  guid: "id"
};

/**
 * One fixed colour per value type, so a parameter list reads by type at a glance and a String is
 * the same green on every action. Anything that is not a plain value — a model, a list, a file —
 * is "other" and stays neutral. `toLowerCase` is locale-independent in JS, so "Int32" never turns
 * into a dotted-i key under a Turkish locale.
 */
export const getParameterTypeTone = (typeName: string): ParameterTypeTone =>
  TYPE_TONES[typeName.toLowerCase()] ?? "other";
